// @flow
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { DatePicker } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import messages from '../../messages';

type Props = {
  // Decides the pre-fill and the picker's minimum, not editability.
  isServerAuthoritative: boolean,
};

// ENG-31746 — always editable, for every customer. The derived date is a pre-fill; what the user
// picks is what persists, bounded server-side by `withinOfferWindow` in leaseOffer.schema.js.
const ExpirationDateField = ({ isServerAuthoritative }: Props) => {
  const { control, trigger } = useFormContext();

  // Mirrors the two branches of makeResolver in ../hooks.js — change one and you have to change
  // the other. Today rather than tomorrow when the server is authoritative, because a SAME_DAY
  // property pre-fills today: a tomorrow minimum would put the field's own pre-filled value out of
  // range and leave it unsubmittable.
  const earliestDate = moment()
    .add(isServerAuthoritative ? 0 : 1, 'day')
    .startOf('day')
    .toDate();
  const maxOfferDate = moment().add(120, 'days').endOf('day').toDate();

  return (
    <>
      <Typography variant="labelLarge" sx={{ mb: 2 }}>
        <FormattedMessage {...messages.offerExpires} /> *
      </Typography>
      <Controller
        name="offerExpirationDate"
        control={control}
        render={({ field, fieldState }) => (
          // The placeholder is a string the picker cannot render as a date; show it as an empty
          // picker so the resolver's "must select a date" is what tells the user, not a broken value.
          <DatePicker
            id="offer-expiration-date"
            value={field.value === 'MM/DD/YYYY' ? null : field.value}
            onChange={(date) => {
              field.onChange(date);
              trigger('offerExpirationDate');
            }}
            onBlur={field.onBlur}
            minDate={earliestDate}
            maxDate={maxOfferDate}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message}
          />
        )}
      />
    </>
  );
};

export default ExpirationDateField;
